import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const BatchFormModal = ({ batch, onClose, onSaved }) => {
  const isEdit = Boolean(batch);

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [form, setForm] = useState({
    batch_no: '',
    product_id: '',
    mfg_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    qty_received: '',
    qty_available: '',
    cost_per_unit: '',
    location: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Load product list when modal opens (fetch all in one go)
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        // Pull a large page so we get them all — chemical module is ~86 products per spec
        const res = await chemicalService.getProducts({ page: 1, limit: 1000 });
        setProducts(res.data || []);
      } catch (err) {
        console.error('Error loading products:', err);
        alert('❌ Failed to load products list');
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  // Hydrate form when editing
  useEffect(() => {
    if (batch) {
      setForm({
        batch_no: batch.batch_no || '',
        product_id: batch.product_id || '',
        mfg_date: batch.mfg_date ? batch.mfg_date.split('T')[0] : '',
        expiry_date: batch.expiry_date ? batch.expiry_date.split('T')[0] : '',
        qty_received: batch.qty_received != null ? batch.qty_received : '',
        qty_available: batch.qty_available != null ? batch.qty_available : '',
        cost_per_unit: batch.cost_per_unit != null ? batch.cost_per_unit : '',
        location: batch.location || '',
        notes: batch.notes || '',
      });
    }
  }, [batch]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // If user types qty_received and qty_available is blank in create mode, mirror it
      if (!isEdit && field === 'qty_received' && (prev.qty_available === '' || prev.qty_available === prev.qty_received)) {
        next.qty_available = value;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.batch_no.trim() || !form.product_id || !form.mfg_date) {
      alert('❌ Batch No, Product, and Mfg Date are required.');
      return;
    }
    if (!isEdit && (form.qty_received === '' || parseFloat(form.qty_received) <= 0)) {
      alert('❌ Qty Received must be greater than 0.');
      return;
    }
    if (form.expiry_date && form.mfg_date && form.expiry_date < form.mfg_date) {
      alert('❌ Expiry date cannot be before Mfg date.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        batch_no: form.batch_no.trim(),
        product_id: parseInt(form.product_id, 10),
        mfg_date: form.mfg_date,
        expiry_date: form.expiry_date || null,
        qty_available: form.qty_available === '' ? null : parseFloat(form.qty_available),
        cost_per_unit: form.cost_per_unit === '' ? null : parseFloat(form.cost_per_unit),
        location: form.location.trim() || null,
        notes: form.notes.trim() || null,
      };

      if (!isEdit) {
        // qty_received only sent on create
        payload.qty_received = parseFloat(form.qty_received);
        // If qty_available wasn't set, backend defaults to qty_received
        if (payload.qty_available === null) {
          payload.qty_available = payload.qty_received;
        }
        await chemicalService.createBatch(payload);
        alert('✅ Batch created successfully!');
      } else {
        await chemicalService.updateBatch(batch.id, payload);
        alert('✅ Batch updated successfully!');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save batch';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Find selected product to show its unit hint
  const selectedProduct = products.find((p) => p.id === parseInt(form.product_id, 10));
  const unitHint = selectedProduct?.base_unit || '';

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>
              {isEdit ? '✏️ Edit Batch' : '➕ Add Batch'}
            </h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              {isEdit ? `Updating ${batch.batch_no}` : 'Record an inventory batch'}
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* ─── Section: Identification ─────── */}
            <div style={styles.sectionLabel}>Identification</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Batch No. <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  value={form.batch_no}
                  onChange={(e) => handleChange('batch_no', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. CHM-2026-027"
                  required
                />
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>
                  Product <span style={styles.required}>*</span>
                </label>
                <select
                  value={form.product_id}
                  onChange={(e) => handleChange('product_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingProducts || isEdit}
                >
                  <option value="">
                    {loadingProducts ? 'Loading products...' : '-- Select product --'}
                  </option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.name}
                    </option>
                  ))}
                </select>
                {isEdit && (
                  <span style={styles.lockedHint}>🔒 Product cannot be changed after creation</span>
                )}
              </div>
            </div>

            {/* ─── Section: Dates ─────────────── */}
            <div style={styles.sectionLabel}>Dates</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Mfg Date <span style={styles.required}>*</span>
                </label>
                <input
                  type="date"
                  value={form.mfg_date}
                  onChange={(e) => handleChange('mfg_date', e.target.value)}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Expiry Date</label>
                <input
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => handleChange('expiry_date', e.target.value)}
                  style={styles.input}
                  min={form.mfg_date || undefined}
                />
              </div>
            </div>

            {/* ─── Section: Quantities ────────── */}
            <div style={styles.sectionLabel}>Quantities {unitHint && <span style={styles.unitChip}>{unitHint}</span>}</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Qty Received {!isEdit && <span style={styles.required}>*</span>}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.qty_received}
                  onChange={(e) => handleChange('qty_received', e.target.value)}
                  style={{ ...styles.input, ...(isEdit ? styles.inputDisabled : {}) }}
                  placeholder={`e.g. 120`}
                  required={!isEdit}
                  disabled={isEdit}
                />
                {isEdit && (
                  <span style={styles.lockedHint}>🔒 Locked after creation</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Qty Available</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.qty_available}
                  onChange={(e) => handleChange('qty_available', e.target.value)}
                  style={styles.input}
                  placeholder={isEdit ? '' : 'Defaults to Qty Received'}
                />
                {!isEdit && (
                  <span style={styles.hint}>Leave blank to use Qty Received</span>
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cost per {unitHint || 'unit'} (Rs)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_per_unit}
                  onChange={(e) => handleChange('cost_per_unit', e.target.value)}
                  style={styles.input}
                  placeholder="Rs"
                />
              </div>
            </div>

            {/* Inline cost preview */}
            {form.qty_available !== '' && form.cost_per_unit !== '' && (
              <div style={styles.costPreview}>
                Stock value: <strong>
                  Rs {Math.round(parseFloat(form.qty_available) * parseFloat(form.cost_per_unit)).toLocaleString('en-PK')}
                </strong>
              </div>
            )}

            {/* ─── Section: Location + Notes ──── */}
            <div style={styles.sectionLabel}>Location & Notes</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Location</label>
                <select
                  value={form.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  style={styles.input}
                >
                  <option value="">None</option>
                  <option value="Warehouse A">Warehouse A</option>
                  <option value="Warehouse B">Warehouse B</option>
                  <option value="Warehouse C">Warehouse C</option>
                  <option value="Warehouse D">Warehouse D</option>
                </select>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '60px', resize: 'vertical', fontFamily: 'inherit' }}
                placeholder="Optional notes about this batch..."
                rows="2"
              />
            </div>

            {/* Source indicator (read-only) */}
            {isEdit && batch.source && batch.source !== 'manual' && (
              <div style={styles.sourceNote}>
                ⓘ This batch was created by <strong>{batch.source}</strong>
                {batch.source_id ? ` #${batch.source_id}` : ''} and is linked to that record.
              </div>
            )}

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : (isEdit ? '💾 Update Batch' : '💾 Create Batch')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '20px',
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '820px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '20px 30px',
    borderBottom: '2px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'sticky', top: 0, background: 'white', zIndex: 10,
  },
  modalTitle: {
    fontSize: '22px', fontWeight: '700',
    color: '#2c3e50', margin: 0,
  },
  closeButton: {
    padding: '8px 16px',
    background: '#f0f0f0', border: 'none',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '20px', fontWeight: '700', color: '#666',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#7f8c8d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '16px',
    marginBottom: '12px',
    paddingBottom: '6px',
    borderBottom: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  unitChip: {
    background: '#e3f2fd', color: '#1565c0',
    padding: '2px 10px', borderRadius: '10px',
    fontSize: '11px', fontWeight: '700',
    textTransform: 'none', letterSpacing: 0,
  },
  row: {
    display: 'flex', gap: '16px', flexWrap: 'wrap',
  },
  formGroup: {
    flex: 1, minWidth: '180px',
    display: 'flex', flexDirection: 'column',
    gap: '6px', marginBottom: '12px',
  },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: {
    width: '100%', padding: '10px 12px',
    border: '2px solid #e0e0e0', borderRadius: '8px',
    fontSize: '14px', boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  inputDisabled: {
    background: '#f5f7fa', color: '#7f8c8d', cursor: 'not-allowed',
  },
  hint: {
    fontSize: '11px', color: '#95a5a6', fontStyle: 'italic',
  },
  lockedHint: {
    fontSize: '11px', color: '#856404', fontWeight: '600',
  },
  costPreview: {
    background: '#e8f5e9', borderRadius: '8px',
    padding: '10px 14px', marginBottom: '12px',
    textAlign: 'center', color: '#155724', fontSize: '14px',
  },
  sourceNote: {
    background: '#fff3cd', borderRadius: '8px',
    padding: '10px 14px', marginTop: '12px',
    color: '#856404', fontSize: '13px',
  },
  modalFooter: {
    padding: '16px 30px',
    borderTop: '2px solid #f0f0f0',
    display: 'flex', justifyContent: 'flex-end', gap: '12px',
    position: 'sticky', bottom: 0, background: 'white',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#95a5a6', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
  submitButton: {
    padding: '12px 28px',
    background: '#e74c3c', color: 'white',
    border: 'none', borderRadius: '8px',
    cursor: 'pointer', fontSize: '14px', fontWeight: '600',
  },
};

export default BatchFormModal;